import React, { useState, useEffect } from "react";
import { Card, Form, InputGroup } from "react-bootstrap";
import ReactSpeedometer from "react-d3-speedometer";
import {
  FaCheckCircle,
  FaTimesCircle,
  FaRegCopy,
  FaCheck,
} from "react-icons/fa";

const PasswordChecker = ({isDark}) => {
  const [password, setPassword] = useState("");
  const [copied, setCopied] = useState(false);
  const [score, setScore] = useState(0);
  const [rules, setRules] = useState({
    length: false,
    number: false,
    special: false,
    lowercase: false,
    uppercase: false,
  });

  useEffect(() => {
    const updatedRules = {
      length: password.length >= 8,
      number: /\d/.test(password),
      special: /[!@#$%^&*(),.?":{}|<>]/.test(password),
      lowercase: /[a-z]/.test(password),
      uppercase: /[A-Z]/.test(password),
    };

    setRules(updatedRules);

    let newScore = 0;
    Object.values(updatedRules).forEach((rule) => {
      if (rule) newScore += 20;
    });

    setScore(newScore);
  }, [password]);

  const copyToClipboard = () => {
    navigator.clipboard.writeText(password);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const RuleItem = ({ valid, text }) => (
    <div className="hstack gap-2" style={{fontSize: 13}}>
      {valid ? (
        <FaCheckCircle className="text-success" />
      ) : (
        <FaTimesCircle className="text-danger" />
      )}
      <span>{text}</span>
    </div>
  );

  const getStrengthLabel = () => {
    if (score <= 20) return `Very Weak ${score}%`;
    if (score <= 40) return `Weak ${score}%`;
    if (score <= 60) return `Medium ${score}%`;
    if (score <= 80) return `Strong ${score}%`;
    return `Very Strong ${score}%`;
  };

  const getCrackTime = () => {
    if (score <= 20) return "Seconds";
    if (score <= 40) return "Minutes";
    if (score <= 60) return "Hours";
    if (score <= 80) return "Years";
    return "Centuries";
  };

  return (
    <Card className={`pin-card shadow-sm border ${!isDark && "bg-light"}`}>
      <h5 className="text-center fw-bold mb-3">Password Strength Checker</h5>

      <InputGroup className="mb-3">
        <Form.Control
          type="text"
          value={password}
          maxLength={100}
          className="password-input interactive-input"
          placeholder="Enter your password"
          onChange={(e) => setPassword(e.target.value)}
        />
        {password && (
          <>
            <InputGroup.Text
              className="CP fw-bold bg-success text-white border-success"
              style={{fontSize: 13}}
              title={"Check Strength"}
            >
              Check Strength
            </InputGroup.Text>
            <InputGroup.Text
              className="copy-btn CP bg-primary border-primary text-white"
              title={copied ? "Copied" : "Copy Password"}
              onClick={copyToClipboard}
            >
              {copied ? <FaCheck /> : <FaRegCopy />}
            </InputGroup.Text>
          </>
        )}
      </InputGroup>

      <div className="text-center speedometer">
        <ReactSpeedometer
          maxValue={100}
          value={score}
          needleHeightRatio={0.7}
          ringWidth={35}
          segments={5}
          segmentColors={[
            "#dc3545",
            "#fd7e14",
            "#ffc107",
            "#0dcaf0",
            "#198754",
          ]}
          needleColor={isDark ? "#fff" : "#000"}
          textColor={isDark ? "#fff" : "#000"}
          currentValueText={getStrengthLabel()}
        />
      </div>

      <div className="rules-box mt-3 mb-3">
        <RuleItem valid={rules.length} text="At least 8 characters" />
        <RuleItem valid={rules.number} text="Contains a number (0–9)" />
        <RuleItem valid={rules.special} text="Contains a special character" />
        <RuleItem valid={rules.lowercase} text="Lowercase letter (a–z)" />
        <RuleItem valid={rules.uppercase} text="Uppercase letter (A–Z)" />
      </div>

      <div className="strength-summary text-center" style={{fontSize: 14}}>
        <p className="mb-1">
          Strength: <b>{getStrengthLabel()}</b>
        </p>
        <p className="mb-0">
          Estimated crack time: <b>{getCrackTime()}</b>
        </p>
      </div>
    </Card>
  );
};

export default PasswordChecker;
